import React, { useState, useEffect } from 'react';
import AppLayout from '../../components/AppLayout';
import ProspectingNav from '../../components/prospecting/ProspectingNav';
import { useTheme } from '../../contexts/ThemeContext';
import { Plus, RefreshCw, Pause, Play, X, Loader2, Clock, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-500', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-500', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-500', icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500', icon: XCircle },
};

export default function ScrapingJobs() {
  const { isDark } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newJob, setNewJob] = useState({ source_url: '', source_type: 'company', max_followers: 500, icp_template_id: '', priority: 5 });
  const [icpTemplates, setIcpTemplates] = useState([]);
  const [queueStats, setQueueStats] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchJobs(); fetchICPs(); fetchQueueStats(); }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/scraping/jobs?limit=50`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setJobs(d.jobs); setTotal(d.total); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchICPs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/icp`, { credentials: 'include' });
      if (res.ok) setIcpTemplates(await res.json());
    } catch (err) { console.error(err); }
  };

  const fetchQueueStats = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/queue/stats`, { credentials: 'include' });
      if (res.ok) setQueueStats(await res.json());
    } catch (err) { console.error(err); }
  };

  const createJob = async () => {
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/prospecting/scraping/jobs`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(newJob),
      });
      if (res.ok) { setShowCreate(false); setNewJob({ source_url: '', source_type: 'company', max_followers: 500, icp_template_id: '', priority: 5 }); fetchJobs(); fetchQueueStats(); }
      else { const err = await res.json(); alert(err.detail || 'Failed to create job'); }
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const updateJob = async (jobId, action) => {
    await fetch(`${BACKEND_URL}/api/prospecting/scraping/jobs/${jobId}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ action }),
    });
    fetchJobs(); fetchQueueStats();
  };

  const card = `${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} border rounded-xl`;
  const input = `w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900'} border`;

  return (
    <AppLayout title="Scraping Jobs">
      <div className="space-y-6" data-testid="scraping-jobs">
        <ProspectingNav />
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Scraping Jobs</h1>
            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Manage LinkedIn follower scraping jobs</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { fetchJobs(); fetchQueueStats(); }} className={`p-2 rounded-lg ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200'}`}>
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => setShowCreate(true)} data-testid="create-job-btn" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] text-sm">
              <Plus className="w-4 h-4" /> New Scraping Job
            </button>
          </div>
        </div>

        {/* Queue Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {['pending', 'processing', 'completed', 'failed', 'cancelled'].map(s => {
            const cfg = STATUS_CONFIG[s];
            const Icon = cfg.icon;
            return (
              <div key={s} className={`${card} p-4 flex items-center gap-3`}>
                <div className={`p-2 rounded-lg ${cfg.color}/20`}><Icon className={`w-4 h-4 ${cfg.color.replace('bg-', 'text-')}`} /></div>
                <div>
                  <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{queueStats[s] || 0}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{cfg.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Jobs List */}
        <div className={`${card} overflow-hidden`}>
          <table className="w-full" data-testid="jobs-table">
            <thead>
              <tr className={`border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Source</th>
                <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Max</th>
                <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Created</th>
                <th className={`text-left p-3 text-xs font-medium uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#A2FF00]" /></td></tr>
              ) : jobs.length === 0 ? (
                <tr><td colSpan="6" className={`p-8 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No scraping jobs yet</td></tr>
              ) : jobs.map(job => {
                const st = STATUS_CONFIG[job.status] || STATUS_CONFIG.pending;
                return (
                  <tr key={job.job_id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                    <td className={`p-3 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      <span className="truncate max-w-[200px] block">{job.payload?.source_url || '-'}</span>
                    </td>
                    <td className={`p-3 text-sm capitalize ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{job.payload?.source_type || '-'}</td>
                    <td className={`p-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{job.payload?.max_followers || '-'}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs font-medium text-white ${st.color}`}>{st.label}</span></td>
                    <td className={`p-3 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{job.created_at ? new Date(job.created_at).toLocaleString() : '-'}</td>
                    <td className="p-3">
                      <div className="flex gap-1">
                        {job.status === 'pending' && <button onClick={() => updateJob(job.job_id, 'cancel')} className="p-1 rounded hover:bg-red-500/20 text-red-400" title="Cancel"><X className="w-4 h-4" /></button>}
                        {job.status === 'processing' && <button onClick={() => updateJob(job.job_id, 'pause')} className="p-1 rounded hover:bg-yellow-500/20 text-yellow-400" title="Pause"><Pause className="w-4 h-4" /></button>}
                        {job.status === 'cancelled' && <button onClick={() => updateJob(job.job_id, 'resume')} className="p-1 rounded hover:bg-green-500/20 text-green-400" title="Resume"><Play className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className={`w-full max-w-lg rounded-2xl p-6 ${isDark ? 'bg-[#111] border border-white/10' : 'bg-white'}`}>
              <h2 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>New Scraping Job</h2>
              <div className="space-y-3">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>LinkedIn URL *</label>
                  <input value={newJob.source_url} onChange={e => setNewJob(p => ({ ...p, source_url: e.target.value }))} placeholder="https://linkedin.com/company/..." className={input} data-testid="job-source-url" />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Source Type</label>
                  <select value={newJob.source_type} onChange={e => setNewJob(p => ({ ...p, source_type: e.target.value }))} className={input}>
                    <option value="company">Company Page</option>
                    <option value="personal">Personal Profile</option>
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Max Followers</label>
                  <input type="number" value={newJob.max_followers} onChange={e => setNewJob(p => ({ ...p, max_followers: parseInt(e.target.value) || 500 }))} className={input} />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>ICP Template (for auto-scoring)</label>
                  <select value={newJob.icp_template_id} onChange={e => setNewJob(p => ({ ...p, icp_template_id: e.target.value }))} className={input}>
                    <option value="">-- No auto-scoring --</option>
                    {icpTemplates.map(t => <option key={t.icp_id} value={t.icp_id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Priority (1=highest, 10=lowest)</label>
                  <input type="number" min="1" max="10" value={newJob.priority} onChange={e => setNewJob(p => ({ ...p, priority: parseInt(e.target.value) || 5 }))} className={input} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button onClick={() => setShowCreate(false)} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
                <button onClick={createJob} disabled={creating || !newJob.source_url} data-testid="submit-job-btn" className="px-4 py-2 rounded-lg text-sm bg-[#A2FF00] text-black font-medium hover:bg-[#8FE600] disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Job'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
